import fs from "fs";
import path from "path";
import { generateTemplate } from "./utils/template-generators";
import { ensureDirectoryExists } from "./utils/cli.helpers";
import {
  camelCase,
  kebabCase,
  pascalCase,
} from "../helpers/change-case.helpers";
import { fullCleanCwd, getUserFileExtension } from "../helpers/fs.helpers";
import sheu from "../sheu";
import { capitalize } from "../helpers/text.helpers";
import prismaSchemaParser from "../prisma/prisma-schema-parser";
import { kebabToHuman } from "../../modules/swagger/utils/helpers/swagger.router.helpers";

const models = prismaSchemaParser
  .getModelsAsArrayOfStrings()
  .map((val) => kebabCase(val));

const knownModules = [...models, "file-upload", "auth"];

interface GenerateOptions {
  path?: string;
  model?: string;
  module?: string;
}

interface GenerateConfig {
  templateName: string;
  fileSuffix: string;
  customValidation?: (modelName: string) => void;
  customImports?: (names: any) => any;
  customPath?: string;
  prefix?: string;
  allowedModules: string[];
}

const generateFile = async (
  options: GenerateOptions,
  config: GenerateConfig
) => {
  const modelName = options.module || options.model;

  if (options.module && options.model)
    throw Error(
      "You must either pass --module or --model, prefer --module to align with future updates."
    );

  if (!modelName?.trim()) throw new Error("Module name is required!");

  const isAllowedModule = config.allowedModules.includes(kebabCase(modelName));
  const isKnowModule = config.allowedModules.includes(kebabCase(modelName));

  if (!isKnowModule)
    throw new Error(
      `Generate command are only available for know modules such as all prisma models, file-upload and auth. And you passed ${modelName}.`
    );
  else if (!isAllowedModule)
    throw Error(
      `${kebabToHuman(kebabCase(config.templateName))} are not available for module ${modelName}`
    );

  if (config.customValidation) config.customValidation(modelName);

  const { path: customPath = "src/modules/{{module-name}}" } = options;

  const names = {
    pascal: pascalCase(modelName),
    camel: camelCase(modelName),
    kebab: kebabCase(modelName),
  };

  const ext = getUserFileExtension();

  const resolvedPath = (config.customPath || customPath).replaceAll(
    "{{module-name}}",
    names.kebab
  );

  const modulePath = path.join(process.cwd(), resolvedPath);

  const fileName = config.prefix
    ? `${config.prefix}${names.kebab}.${config.fileSuffix}.${ext}`
    : `${names.kebab}.${config.fileSuffix}.${ext}`;

  const filePath = path.join(modulePath, fileName);

  const humamReadableTemplateName =
    config.templateName.charAt(0).toUpperCase() +
    config.templateName.slice(1).replaceAll("-", " ");

  try {
    ensureDirectoryExists(modulePath);

    const templateData = {
      modelName: names,
      ...(config.customImports && { imports: config.customImports(names) }),
    };

    const content = generateTemplate(config.templateName, templateData);
    if (fs.existsSync(filePath))
      throw new Error(
        `${capitalize(humamReadableTemplateName.toLowerCase())} for ${options.model} already exists.`
      );
    fs.writeFileSync(filePath, content);

    console.info("");
    sheu.done(
      `${humamReadableTemplateName} for ${options.model} generated under ${fullCleanCwd(filePath)}`
    );
  } catch (err: any) {
    console.info("");
    sheu.error(
      `Failed because of ${err?.message?.toLowerCase() || "unknown reason"}`
    );
    process.exit(1);
  }
};

export const generateCommand = {
  controller: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "controller",
      fileSuffix: "controller",
      customImports: () => ({
        baseController: "arkos/controllers",
      }),
      allowedModules: knownModules,
    });
  },

  service: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "service",
      fileSuffix: "service",
      customImports: () => ({
        baseService: "arkos/services",
      }),
      allowedModules: knownModules,
    });
  },

  router: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "router",
      fileSuffix: "router",
      customImports: (names) => ({
        baseRouter: "arkos",
        controller: `./${names.kebab}.controller`,
      }),
      allowedModules: knownModules,
    });
  },

  interceptors: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "interceptors",
      fileSuffix: "interceptors",
      allowedModules: knownModules,
    });
  },

  authConfigs: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "auth-configs",
      fileSuffix: "auth",
      allowedModules: knownModules,
    });
  },

  hooks: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "hooks",
      fileSuffix: "hooks",
      allowedModules: knownModules,
    });
  },

  createSchema: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "create-schema",
      fileSuffix: "schema",
      customPath: "src/modules/{{module-name}}/schemas",
      prefix: "create-",
      allowedModules: models,
    });
  },

  updateSchema: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "update-schema",
      fileSuffix: "schema",
      customPath: "src/modules/{{module-name}}/schemas",
      prefix: "update-",
      allowedModules: models,
    });
  },

  createDto: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "create-dto",
      fileSuffix: "dto",
      customPath: "src/modules/{{module-name}}/dtos",
      prefix: "create-",
      allowedModules: models,
    });
  },

  updateDto: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "update-dto",
      fileSuffix: "dto",
      customPath: "src/modules/{{module-name}}/dtos",
      prefix: "update-",
      allowedModules: models,
    });
  },

  queryOptions: async (options: GenerateOptions) => {
    await generateFile(options, {
      templateName: "query-options",
      fileSuffix: "query",
      allowedModules: knownModules,
    });
  },
};
